import { TenantAccount } from '../gql-types.generated';

// Contract with ezpay-api-svc's assistant pass-through (AB#941100). The backend enforces every rule here as well;
// the portal only hides what would not work.
export const assistantMessageMaxLength = 4000;

export interface MerchantAssistantVisibilityInput {
  isApteanAdmin: boolean;
  selectedTenantId?: string;
  tenantAccount?: Pick<TenantAccount, 'owner' | 'settings'> | null;
}

/**
 * The panel is shown only to an aptean-admin who is acting as a tenant whose merchantAssistant flag is on.
 * The tenant account must be the selected one: on a tenant switch selectedTenantId changes before the new
 * account is fetched, and the old account's flag must not leak onto the new tenant.
 */
export const canShowMerchantAssistant = ({
  isApteanAdmin,
  selectedTenantId,
  tenantAccount,
}: MerchantAssistantVisibilityInput): boolean => {
  if (!isApteanAdmin || !selectedTenantId || !tenantAccount) return false;
  if (tenantAccount.owner?.tenantId !== selectedTenantId) return false;
  return tenantAccount.settings?.features?.merchantAssistant === true;
};

/**
 * The assistant is served by the same API host as GraphQL, at /assistant/chat. The configured endpoint is the
 * GraphQL URL, which is the host root in every tier (e.g. https://dev.api.apteanpay.com/); a trailing /graphql is
 * stripped as well in case a tier ever points at it.
 */
export const getAssistantChatUrl = (graphQlEndpoint: string): string => {
  const base = graphQlEndpoint
    .trim()
    .replace(/\/+$/, '')
    .replace(/\/graphql$/i, '');
  return `${base}/assistant/chat`;
};

export interface SseEvent {
  event: string;
  data: string;
}

export interface SseParser {
  push: (chunk: string) => void;
  flush: () => void;
}

/**
 * Incremental text/event-stream parser. Chunks may split anywhere, including inside a line or between the CR and
 * LF of a CRLF. Lines starting with ':' are comments (keepalives) and are ignored.
 */
export const createSseParser = (onEvent: (event: SseEvent) => void): SseParser => {
  let buffer = '';
  let eventName = '';
  let dataLines: string[] = [];

  const dispatch = () => {
    if (dataLines.length > 0 || eventName) {
      onEvent({ event: eventName || 'message', data: dataLines.join('\n') });
    }
    eventName = '';
    dataLines = [];
  };

  const processLine = (line: string) => {
    if (line === '') {
      dispatch();
      return;
    }
    if (line.startsWith(':')) return;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') eventName = value;
    else if (field === 'data') dataLines.push(value);
  };

  const drain = (final: boolean) => {
    for (;;) {
      const match = /\r\n|\r|\n/.exec(buffer);
      if (!match) break;
      // A trailing CR may be the first half of a CRLF still in flight.
      if (match[0] === '\r' && match.index === buffer.length - 1 && !final) break;
      processLine(buffer.slice(0, match.index));
      buffer = buffer.slice(match.index + match[0].length);
    }
    if (final) {
      if (buffer) processLine(buffer);
      buffer = '';
      dispatch();
    }
  };

  return {
    push: (chunk: string) => {
      buffer += chunk;
      drain(false);
    },
    flush: () => drain(true),
  };
};

export enum AssistantErrorKind {
  Unavailable = 'unavailable',
  SessionExpired = 'sessionExpired',
  Forbidden = 'forbidden',
  NotEnabled = 'notEnabled',
  BadRequest = 'badRequest',
  Stream = 'stream',
  Network = 'network',
  Unknown = 'unknown',
}

export interface AssistantError {
  kind: AssistantErrorKind;
  message: string;
  statusCode?: number;
}

const readCode = (body: unknown): string | undefined => {
  if (body && typeof body === 'object' && typeof (body as { code?: unknown }).code === 'string') {
    return (body as { code: string }).code;
  }
  return undefined;
};

/** Maps a non-200 response from /assistant/chat to the message shown in the panel. */
export const mapAssistantErrorResponse = (statusCode: number, body?: unknown): AssistantError => {
  const code = readCode(body);
  if (statusCode === 404) {
    return { kind: AssistantErrorKind.Unavailable, message: 'The assistant is not available.', statusCode };
  }
  if (statusCode === 401) {
    return {
      kind: AssistantErrorKind.SessionExpired,
      message: 'Your session has expired. Sign in again to keep using the assistant.',
      statusCode,
    };
  }
  if (statusCode === 403) {
    if (code === 'ASSISTANT_NOT_ENABLED') {
      return { kind: AssistantErrorKind.NotEnabled, message: 'The assistant is not enabled for this tenant.', statusCode };
    }
    // ASSISTANT_FORBIDDEN, or a 403 from something in front of the API.
    return { kind: AssistantErrorKind.Forbidden, message: 'The assistant is only available to Aptean administrators.', statusCode };
  }
  if (statusCode === 400) {
    return {
      kind: AssistantErrorKind.BadRequest,
      message: `The assistant could not accept that message. Messages must be 1 to ${assistantMessageMaxLength} characters.`,
      statusCode,
    };
  }
  return { kind: AssistantErrorKind.Unknown, message: `The assistant could not answer (HTTP ${statusCode}). Try again.`, statusCode };
};

export interface AssistantStreamHandlers {
  onToken: (text: string) => void;
  onMessage: (text: string) => void;
  onError: (error: AssistantError) => void;
  onDone: () => void;
}

const parseJson = (data: string): Record<string, unknown> | undefined => {
  try {
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    return undefined;
  }
};

/** Routes one parsed SSE event to the handler for its type. Returns true once the turn has ended. */
export const handleAssistantEvent = (event: SseEvent, handlers: AssistantStreamHandlers): boolean => {
  switch (event.event) {
    case 'token': {
      const text = parseJson(event.data)?.text;
      if (typeof text === 'string') handlers.onToken(text);
      return false;
    }
    case 'message': {
      const text = parseJson(event.data)?.text;
      if (typeof text === 'string') handlers.onMessage(text);
      return false;
    }
    case 'error': {
      const message = parseJson(event.data)?.message;
      handlers.onError({
        kind: AssistantErrorKind.Stream,
        message: typeof message === 'string' && message ? message : 'The assistant hit an error while answering.',
      });
      return false;
    }
    case 'done':
      handlers.onDone();
      return true;
    default:
      return false;
  }
};

export interface AssistantChatRequest {
  url: string;
  headers: Record<string, string>;
  message: string;
  conversationId: string;
  signal?: AbortSignal;
}

const readErrorBody = async (response: Response): Promise<unknown> => {
  try {
    const text = await response.text();
    return text ? JSON.parse(text) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Sends one turn and streams the answer. Uses fetch rather than EventSource because EventSource can neither POST
 * nor send the Authorization and x-aptean-tenant headers. onDone is always called last, even when the stream ends
 * without a done event; an aborted request calls nothing.
 */
export const streamAssistantChat = async (request: AssistantChatRequest, handlers: AssistantStreamHandlers): Promise<void> => {
  let finished = false;
  const finish = () => {
    if (!finished) {
      finished = true;
      handlers.onDone();
    }
  };
  let response: Response;
  try {
    response = await fetch(request.url, {
      method: 'POST',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      headers: { ...request.headers, 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify({ message: request.message, conversationId: request.conversationId }),
      signal: request.signal,
    });
  } catch (e) {
    if (request.signal?.aborted) return;
    handlers.onError({
      kind: AssistantErrorKind.Network,
      message: 'Could not reach the assistant. Check your connection and try again.',
    });
    finish();
    return;
  }

  if (!response.ok) {
    handlers.onError(mapAssistantErrorResponse(response.status, await readErrorBody(response)));
    finish();
    return;
  }
  if (!response.body) {
    handlers.onError({ kind: AssistantErrorKind.Stream, message: 'The assistant returned no answer.' });
    finish();
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser(event => {
    if (!finished && handleAssistantEvent(event, { ...handlers, onDone: finish })) finish();
  });
  try {
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      if (done) break;
      parser.push(decoder.decode(value, { stream: true }));
    }
    parser.push(decoder.decode());
    parser.flush();
  } catch (e) {
    if (request.signal?.aborted) return;
    handlers.onError({ kind: AssistantErrorKind.Network, message: 'The connection to the assistant was interrupted. Try again.' });
  }
  finish();
};
